import React, { useCallback, useContext } from 'react';
import { DocumentCarouselContext, IActionCriteria, IDocumentPrint } from '../../components/document/DocumentProvider';
import { useQuery } from '@apollo/client';
import {
  IDispatchInstruction,
  IDispatchInstructionDocumentCarouselQueryResult,
  IDispatchInstructionDocumentCarouselQueryVariables,
  IDispatchInstructionReportMutationResult,
  IDispatchInstructionReportMutationVariables,
  ReportType,
} from '../../graphql/schema/p4types';
import {
  getDocumentCarouselReportParameters,
  IDocumentValues,
  useActionFilters,
  useDocumentCarouselDefaults,
  useSalesOrderDeliveryRecipient,
} from '../../components/document/documentCarouselUtil';
import DocumentCarouselHeader from '../../components/document/DocumentCarouselHeader';
import { FormProvider, useForm } from 'react-hook-form';
import DocumentCarouselTemplate from '../../components/document/DocumentCarouselTemplate';
import DocumentCarouselOutput from '../../components/document/DocumentCarouselOutput';
import DocumentCarouselButtons from '../../components/document/DocumentCarouselButtons';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import { useDocumentCarouselComplete, useDocumentJobCompletion } from '../../utils/DocumentUtils';
import getClientMutationId from '../../utils/getClientMutationId';
import DocumentCarouselEmail from '../../components/document/DocumentCarouselEmail';
import DispatchInstructionDocumentCarouselQuery from '../../graphql/queries/document/DispatchInstructionDocumentCarouselQuery.graphql';
import DocumentCarouselPrintOptions from '../../components/document/DocumentCarouselPrintOptions';
import DocumentCarouselSummary from '../../components/document/DocumentCarouselSummary';
import { useCookies } from 'react-cookie';
import { DocumentCarouselHeaderInfo } from '../../components/document/DocumentCarouselHeaderInfo';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import DocumentCarouselSkeleton from '../../components/document/DocumentCarouselSkeleton';
import { useSalesOrderConfig } from '../../utils/FetchConfig';
import ConformityCertificate from '../../graphql/entities/sales/ConformityCertificate';
import DocumentCarouselResetToStart from '../../components/document/DocumentCarouseResetToStart';

//Note - DocumentModal2 currently uses 'documentSummary' instead of 'DOCUMENT_SUMMARY' - Possible cause for minor friction in transition.
export const DOCUMENT_SUMMARY = 'DOCUMENT_SUMMARY';
export const PRINT_INDIVIDUAL_PREFERENCE_COOKIE = 'PRINT_INDIVIDUAL_PREFERENCE_COOKIE';

/**
 * Render the content for the Dispatch Instruction Document Carousel.
 * All querying should be completed before rendering this element so that 'documentDefaults' are accurate/up-to-date when the user see's them.
 * @param documentDefaults - Default document values. Should be final on initial load.
 * @param actionCriteria - The action criteria driving this particular print.
 * @param count - How many records are in this print job.
 * @param singleRecipient - Flag to indicate if all records are a single Customer/Supplier.
 * @param singlePrint - Flag to indicate if we're printing a single record.
 * @param singlePrintNode - If we're printing a single record, this should be the document-queried subset of that record for display/defaulting.
 *                        - Note - Will still be populated for non-single, will be arbitrarily the first record matching the criteria.
 * @param setCookie - Pass-through of cookie setter, used to update 'showSummary' and 'printContiguous' cookie values.
 * @param documentPrint - Print action object - Provides additional detail such as whether this is a reprint, print completion callback or additional email configuration.
 */
const ConformityCertificateDocumentContent = ({
  documentDefaults,
  actionCriteria,
  count,
  singleRecipient,
  singlePrint,
  singlePrintNode,
  setCookie,
  documentPrint,
}: {
  documentDefaults: IDocumentValues;
  actionCriteria: IActionCriteria;
  count: number;
  singleRecipient: boolean;
  singlePrint: boolean;
  singlePrintNode: IDispatchInstruction;
  setCookie: ReturnType<typeof useCookies>[1];
  documentPrint: IDocumentPrint;
}) => {
  const { onSkip } = useContext(DocumentCarouselContext);
  const { onPrintComplete } = documentPrint;

  //Use react-hook-form to manage our form values for better typing and performance than ConnectedForm.
  const methods = useForm<IDocumentValues>({
    defaultValues: documentDefaults,
  });
  const { getValues, watch } = methods;

  const onDocumentComplete = useDocumentCarouselComplete({
    watch,
    entity: ConformityCertificate,
  });

  const onJobStart = useCallback(() => {
    onSkip(true);
  }, [onSkip]);

  const onJobComplete = useDocumentJobCompletion({
    evictionFieldNames: ['salesOrder'],
    onDocumentComplete,
    onPrintComplete,
  });

  const [printConformityCertificatesRange] = useBackgroundMutation<
    IDispatchInstructionReportMutationVariables,
    IDispatchInstructionReportMutationResult
  >(ConformityCertificate.generateRange!, {
    onStartJob: onJobStart,
    onCompleteJob: onJobComplete,
  });

  const onPrintClick = useCallback(() => {
    const reportParameters = getDocumentCarouselReportParameters({ getValues, reprint: false });

    printConformityCertificatesRange({
      variables: {
        input: {
          filters: actionCriteria.filters,
          dispatchInstructions: normalizeVariables(actionCriteria.actionEntities),
          include: actionCriteria.include,
          clientMutationId: getClientMutationId(),
          reportParameters: reportParameters,
        },
      },
    });
  }, [
    actionCriteria.actionEntities,
    actionCriteria.filters,
    actionCriteria.include,
    getValues,
    printConformityCertificatesRange,
  ]);

  const additionalRecipient = useSalesOrderDeliveryRecipient(singlePrintNode?.salesOrder);

  return (
    <FormProvider {...methods}>
      <DocumentCarouselHeader
        entity={ConformityCertificate}
        count={count}
        secondaryTitle={
          <DocumentCarouselHeaderInfo
            contentLeft={singlePrint ? `Sales Order ${singlePrintNode?.salesOrder?.orderNumber}` : undefined}
            contentRight={
              singleRecipient ? `Customer ${singlePrintNode?.salesOrder?.customer?.name ?? 'Unknown'}` : undefined
            }
          />
        }
        reprint={false}
      />
      <div style={{ padding: '1em' }}>
        <DocumentCarouselTemplate reportType={ReportType.ConformityCertificate} singleRecipient={singleRecipient} />
        <DocumentCarouselOutput singleRecipient={singleRecipient} />
        <DocumentCarouselSummary singlePrint={singlePrint} setCookie={setCookie} />
        <DocumentCarouselPrintOptions singlePrint={singlePrint} setCookie={setCookie} />
        <DocumentCarouselResetToStart />
        <DocumentCarouselEmail
          entity={ConformityCertificate}
          recipient={documentDefaults?.recipientForEmail ?? singlePrintNode?.salesOrder?.customer}
          singleRecipient={singleRecipient}
          emailOptions={{ additionalRecipients: additionalRecipient ? [additionalRecipient] : [] }}
        />
      </div>
      <DocumentCarouselButtons onPrint={onPrintClick} />
    </FormProvider>
  );
};

/**
 * @param actionCriteria - The action criteria driving this particular print.
 * @param documentPrint - Print action object - Provides additional detail such as whether this is a reprint, print completion callback or additional email configuration.
 */
export const ConformityCertificateDocumentCarouselCard = ({
  actionCriteria,
  documentPrint,
}: {
  actionCriteria: IActionCriteria;
  documentPrint: IDocumentPrint;
}) => {
  const filters = useActionFilters(actionCriteria);
  const { data: dispatchInstructionData, loading: dispatchInstructionLoading } = useQuery<
    IDispatchInstructionDocumentCarouselQueryResult,
    IDispatchInstructionDocumentCarouselQueryVariables
  >(DispatchInstructionDocumentCarouselQuery, {
    variables: {
      filters: filters,
      first: 2,
      aggregateCriteria: {
        groupColumns: ['salesOrder.customer.id'],
        sumColumns: ['id'],
      },
    },
  });

  const singlePrint: boolean = actionCriteria.include && actionCriteria.actionEntities.length === 1;
  //If the grouping gives a single Customer in the result, we can pull the document defaults off the Customer record and use it for the whole print.
  //(If there's >1 Customer, we cannot do this).
  const singleRecipient = dispatchInstructionData?.listData?.aggregateRows?.length === 1;
  //Note - Take care to always check 'singleRecipient' before consuming this node - singleRecipient *must* be true if consuming this node to avoid bugs.
  //       (This is because for multiple recipients, each node likely has different defaults and we'd only load the 'first' nodes defaults if we use this, potentially overriding the other values with them).
  const singlePrintNode = dispatchInstructionData?.listData?.edges?.[0]?.node as Maybe<IDispatchInstruction>;

  const customerConformityCertificateDefault =
    singlePrintNode?.salesOrder?.customer?.customerConformityCertificateDefault;
  const conformityCertificateDefault = customerConformityCertificateDefault?.conformityCertificateDefault;

  const [cookies, setCookie] = useCookies([DOCUMENT_SUMMARY, PRINT_INDIVIDUAL_PREFERENCE_COOKIE]);
  const salesOrderConfig = useSalesOrderConfig();
  const documentDefaults = useDocumentCarouselDefaults({
    entity: ConformityCertificate,
    recipientDocumentDefault: customerConformityCertificateDefault,
    commonDocumentDefault: conformityCertificateDefault,
    singleRecipient,
    recipient: singlePrintNode?.customer,
    cookies,
    salesOrderConfig,
  });

  if (dispatchInstructionData && !dispatchInstructionLoading) {
    return (
      <ConformityCertificateDocumentContent
        documentDefaults={documentDefaults}
        setCookie={setCookie}
        actionCriteria={actionCriteria}
        count={dispatchInstructionData?.listData?.count ?? 0}
        singleRecipient={singleRecipient}
        singlePrint={singlePrint}
        singlePrintNode={singlePrintNode}
        documentPrint={documentPrint}
      />
    );
  }

  return <DocumentCarouselSkeleton />;
};
